import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Mail, AlertCircle, ArrowLeft, KeyRound, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";
import { recoveryPassword } from "api/auth";
import LoginAside from "./LoginAside";

const RECOVERY_METHODS = ["link", "code"];

export default function PasswordReset() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Quien vuelve desde la pantalla del código llega con su correo y método en el state
  // del router (no en la URL: Clarity graba las URLs). Se valida porque history.state
  // es editable y no debe llegar al formulario con otro tipo.
  const { state } = useLocation();
  const initialEmail = typeof state?.email === "string" ? state.email : "";
  const initialMethod = RECOVERY_METHODS.includes(state?.method) ? state.method : "link";
  const { control, handleSubmit, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deliveryError, setDeliveryError] = useState(null);
  const [method, setMethod] = useState(initialMethod);

  const submitEmail = async ({ email }) => {
    setLoading(true);
    setSuccess(false);
    setDeliveryError(null);

    const result = await recoveryPassword({ email, method });
    setLoading(false);

    // El backend responde 200 tanto si el correo está registrado como si no, así que un
    // fallo acá NUNCA depende de la existencia de la cuenta: o el cliente superó su
    // límite de peticiones (429), o la petición no llegó a destino. Mostrarlos no filtra
    // nada, y callarlos dejaba al usuario esperando un correo que jamás se pidió.
    if (!result.success) {
      setDeliveryError(result.error?.code === "RATE_LIMITED" ? "RATE_LIMITED" : "REQUEST_FAILED");
      return;
    }

    if (method === "code") {
      // El correo viaja en el state del router y no en la query: así no queda en la URL
      // que graba Clarity, y igual sobrevive a un reload (history.state).
      navigate("/login/recovery/code", { state: { email, justSent: true } });
      return;
    }
    setSuccess(true);
  };

  return (
    <div className="login-split">
      <LoginAside />
      <main className="login-main">
        <div className="login-main__inner">
          <div className="login-logo-container login-logo-container--mobile">
            <img src={BeartrackLogo} alt="BearTrack" className="login-logo" />
          </div>

          <div className="login-header">
            <h1 className="login-title">{t("Recupera tu cuenta")}</h1>
            <p className="login-subtitle">{t("Elige cómo quieres recuperar el acceso a tu cuenta.")}</p>
          </div>

          {success ? (
            <div className="login-success">
              <span className="login-success-icon"><CheckCircle size={28} /></span>
              <p className="login-success-text">
                {t("Si existe una cuenta vinculada a ese correo, recibirás un email con los pasos para recuperarla.")}
              </p>
              {/* El mensaje es idéntico exista o no la cuenta, así que quien se equivocó al
                  escribir su correo no recibe nada y no sabe por qué. Esta pista es lo único
                  que se puede dar sin revelar si la cuenta existe. */}
              <p className="login-subtitle" style={{ margin: 0 }}>
                {method === "link"
                  ? t("Revisa que el correo esté bien escrito y mira también la carpeta de spam. El enlace vence en 12 horas.")
                  : t("Revisa que el correo esté bien escrito y mira también la carpeta de spam.")}
              </p>
              <a href="/login" className="login-back-link">
                <ArrowLeft size={16} /> {t("Volver al inicio de sesión")}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit(submitEmail)} className="login-form" noValidate>
              {deliveryError && (
                <div className="login-error" role="alert">
                  <AlertCircle size={14} />
                  {deliveryError === "RATE_LIMITED"
                    ? t("Demasiados intentos. Espera unos minutos antes de volver a solicitarlo.")
                    : t("No pudimos enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.")}
                </div>
              )}

              <div className="login-form-group">
                <label className="login-label" id="recovery-method-label">{t("Método de recuperación")}</label>
                <div className="login-method" role="radiogroup" aria-labelledby="recovery-method-label">
                  <button
                    type="button"
                    onClick={() => setMethod("link")}
                    className={`login-method-option ${method === "link" ? "is-active" : ""}`}
                    role="radio"
                    aria-checked={method === "link"}
                  >
                    <Link2 size={18} aria-hidden="true" />
                    <span>{t("Enlace por correo")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("code")}
                    className={`login-method-option ${method === "code" ? "is-active" : ""}`}
                    role="radio"
                    aria-checked={method === "code"}
                  >
                    <KeyRound size={18} aria-hidden="true" />
                    <span>{t("Código de un solo uso")}</span>
                  </button>
                </div>
                <p className="login-method-hint">
                  {method === "link"
                    ? t("Te enviaremos un enlace para crear una nueva contraseña.")
                    : t("Te enviaremos un código para ingresar directamente, sin cambiar tu contraseña.")}
                </p>
              </div>

              <div className="login-form-group">
                <div className="login-field">
                  <label htmlFor="recovery-email" className="login-label">{t("Correo electrónico")}</label>
                  <div className="login-input-wrap">
                    <span className="login-input-icon"><Mail size={18} strokeWidth={2} /></span>
                    <Controller
                      name="email"
                      control={control}
                      defaultValue={initialEmail}
                      rules={{ required: t("Ingrese un correo válido.") }}
                      render={({ field }) => (
                        <input
                          {...field}
                          id="recovery-email"
                          type="email"
                          autoComplete="email"
                          className="login-input"
                          placeholder={t("tucorreo@gmail.com")}
                        />
                      )}
                    />
                  </div>
                  {errors.email && (
                    <div className="login-error" role="alert">
                      <AlertCircle size={14} />
                      {errors.email.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="login-actions">
                <Button
                  type="submit"
                  className={`login-button ${loading ? "loading" : ""}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {t("Enviando…")}
                    </>
                  ) : (
                    method === "link" ? t("Enviar enlace de recuperación") : t("Enviar código")
                  )}
                </Button>

                <div className="login-forgot">
                  <a href="/login" className="login-forgot-link">{t("Volver al inicio de sesión")}</a>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
